import { DownOutlined } from '@ant-design/icons'
import { Icon } from '@buggy/shared'
import {
  Button,
  Checkbox,
  CheckboxOptionType,
  DatePicker,
  DrawerProps,
  Flex,
  Tag,
} from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { isEqual, uniq } from 'lodash-es'
import isEmpty from 'lodash-es/isEmpty'
import { FunctionComponent, RefObject, useState } from 'react'
import { useDeepCompareEffect } from 'react-use'
import styled, { useTheme } from 'styled-components'
import BasicDrawer from '@/components/common/antd/basic-drawer'
import CustomizedTagSelector from '@/components/search/filters/common/customized-tag-selector'
import { DEFAULT_DATE_FORMAT } from '@/constants/date-format'
import useCustomizedTag from '@/hooks/use-customized-tag'
import { useIntl } from '@/i18n/hooks/use-intl'
import { Optional } from '@/types/optional'
import { PostType, postTypeSchema } from '@/types/schema/post-type'
import { PlatformShortcode } from '@/utils/convert-platform'
import getPostTypeLabel from '@/utils/detail/get-post-type-label'

interface FilterData {
  customizedTagsNames?: string[]
  postTypes?: PostType[]
  postDateRange?: [Dayjs, Dayjs]
}

interface AdvancedFilterDrawerProps
  extends Pick<DrawerProps, 'open' | 'onClose'> {
  containerRef?: RefObject<HTMLElement>
  data?: FilterData
  isKolInfoLocked: boolean
  onClickClear: VoidFunction
  onClickApply: (data: FilterData) => void
  platform: PlatformShortcode
}

const AdvancedFilterDrawer: FunctionComponent<AdvancedFilterDrawerProps> = ({
  open,
  onClose,
  containerRef,
  data,
  isKolInfoLocked,
  onClickClear,
  onClickApply,
  platform,
}) => {
  const { formatMessage } = useIntl()
  const theme = useTheme()
  const { parseFilterTags, getCustomizedTagLocalizedName } = useCustomizedTag()
  const [customizedTagsNames, setCustomizedTagsNames] = useState<
    Optional<string[]>
  >(data?.customizedTagsNames)
  const [postTypes, setPostTypes] = useState<Optional<PostType[]>>(
    data?.postTypes,
  )
  const [postDateRange, setPostDateRange] = useState<Optional<[Dayjs, Dayjs]>>(
    data?.postDateRange,
  )

  const postTypeOptions: CheckboxOptionType<PostType>[] = [
    {
      value: postTypeSchema.enum.image,
      label: getPostTypeLabel(
        postTypeSchema.enum.image,
        platform,
        formatMessage,
      ),
    },
    ...([PlatformShortcode.Instagram, PlatformShortcode.YouTube].includes(
      platform,
    )
      ? [
          {
            value: postTypeSchema.enum.short,
            label:
              platform === PlatformShortcode.Instagram ? 'Reels' : 'Shorts',
          },
        ]
      : []),
    ...(platform === PlatformShortcode.Facebook
      ? [
          {
            value: postTypeSchema.enum.live,
            label: getPostTypeLabel(
              postTypeSchema.enum.live,
              platform,
              formatMessage,
            ),
          },
        ]
      : []),
    {
      value: [PlatformShortcode.TikTok].includes(platform)
        ? postTypeSchema.enum.short
        : postTypeSchema.enum.video,
      label: getPostTypeLabel(
        postTypeSchema.enum.video,
        platform,
        formatMessage,
      ),
    },
  ]

  useDeepCompareEffect((): void => {
    if (isEqual(data?.customizedTagsNames, customizedTagsNames)) {
      return
    }

    setCustomizedTagsNames(data?.customizedTagsNames)
  }, [data?.customizedTagsNames])

  useDeepCompareEffect((): void => {
    if (isEqual(data?.postTypes, postTypes)) {
      return
    }

    setPostTypes(data?.postTypes)
  }, [data?.postTypes])

  useDeepCompareEffect((): void => {
    if (isEqual(data?.postDateRange, postDateRange)) {
      return
    }

    setPostDateRange(data?.postDateRange)
  }, [data?.postDateRange])

  return (
    <BasicDrawer
      closeIcon={false}
      footer={
        <Flex gap={15} justify='end'>
          <Button
            onClick={(): void => {
              setCustomizedTagsNames(undefined)
              setPostTypes(undefined)
              setPostDateRange(undefined)
              onClickClear()
            }}
          >
            {formatMessage({ id: 'general:reset_condition_btn' })}
          </Button>
          <Button
            disabled={isKolInfoLocked}
            icon={isKolInfoLocked ? <Icon name='lock' /> : undefined}
            type='primary'
            onClick={(): void =>
              onClickApply({
                customizedTagsNames,
                postTypes,
                postDateRange,
              })
            }
          >
            {formatMessage({ id: 'general:apply_btn' })}
          </Button>
        </Flex>
      }
      getContainer={containerRef?.current ?? undefined}
      open={open}
      title={formatMessage({ id: 'search:advanced_title' })}
      width={460}
      zIndex={1001}
      onClose={onClose}
    >
      <Flex vertical gap={20}>
        {platform !== PlatformShortcode.Threads && (
          <Flex vertical gap={5}>
            <FilterTitle>
              {formatMessage({ id: 'kol:content_filter_post_type' })}
            </FilterTitle>
            <Checkbox.Group
              options={postTypeOptions}
              value={postTypes}
              onChange={(checkedValue): void => {
                setPostTypes(
                  uniq([
                    ...checkedValue,
                    ...(checkedValue.includes(postTypeSchema.enum.image)
                      ? [postTypeSchema.enum.image, postTypeSchema.enum.text]
                      : []),
                    ...(checkedValue.includes(postTypeSchema.enum.video) &&
                    platform === PlatformShortcode.All
                      ? [postTypeSchema.enum.short, postTypeSchema.enum.live]
                      : []),
                  ]),
                )
              }}
            />
          </Flex>
        )}
        <Flex vertical gap={5}>
          <FilterTitle>
            {formatMessage({ id: 'kol:content_filter_customized_tags' })}
          </FilterTitle>
          <CustomizedTagSelector
            customizedTagsNames={customizedTagsNames?.join('|')}
            mode='multiple'
            onValuesChange={(value): void => {
              setCustomizedTagsNames(parseFilterTags(value))
            }}
          >
            <HackSelectorWrapper>
              <div>
                {isEmpty(customizedTagsNames) || !customizedTagsNames
                  ? formatMessage({ id: 'kol:field_skill_placeholder' })
                  : customizedTagsNames.map((name) => (
                      <Tag
                        closable
                        key={name}
                        onClose={(): void =>
                          setCustomizedTagsNames((prevState) =>
                            prevState?.filter(
                              (selectedName) => selectedName !== name,
                            ),
                          )
                        }
                      >
                        {getCustomizedTagLocalizedName(name)}
                      </Tag>
                    ))}
              </div>
              <DownOutlined />
            </HackSelectorWrapper>
          </CustomizedTagSelector>
        </Flex>
        <Flex vertical gap={5}>
          <FilterTitle>
            {formatMessage({ id: 'search:filter_publish_time_label' })}
          </FilterTitle>
          <RangePicker
            allowClear
            disabledDate={(current): boolean =>
              current &&
              (current < dayjs().subtract(1, 'years').endOf('day') ||
                current > dayjs())
            }
            format={DEFAULT_DATE_FORMAT}
            placeholder={[
              formatMessage({ id: 'general:startdate_placeholder' }),
              formatMessage({ id: 'general:enddate_placeholder' }),
            ]}
            separator='~'
            size='small'
            style={{ width: '100%' }}
            suffixIcon={<Icon color={theme.colors.gray7} name='calendar' />}
            value={postDateRange}
            onChange={(rangeValue): void => {
              const [startDate, endDate] = rangeValue ?? []

              if (!startDate || !endDate) {
                setPostDateRange(undefined)
                return
              }

              setPostDateRange([startDate, endDate])
            }}
          />
        </Flex>
      </Flex>
    </BasicDrawer>
  )
}

const FilterTitle = styled.div`
  color: ${({ theme }): string => theme.colors.gray8};
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 22px; /* 157.15% */
`

const HackSelectorWrapper = styled.div`
  cursor: pointer;
  padding: 5px 11px;
  display: flex;
  min-height: 32px;
  justify-content: space-between;
  align-items: center;
  border: 1px solid ${({ theme }): string => theme.colors.gray5};
  border-radius: 4px;
  color: ${({ theme }): string => theme.colors.gray7};

  svg {
    width: 10px;
    height: 10px;
    color: rgb(0, 0, 0, 0.65);
  }

  .ant-tag {
    margin-right: 4px;
  }
`

const RangePicker = styled(DatePicker.RangePicker)`
  .ant-picker-input > input {
    text-align: center;

    :placeholder-shown {
      text-align: center;
    }
  }
`

export default AdvancedFilterDrawer
